/**
 * Task Notification Service
 *
 * Polls the task API and fires OS-level notifications via the Service Worker when:
 *  1. A new task is assigned to the current user
 *  2. A task has not been updated within the configured stale threshold
 *  3. A task is due within 24 hours
 *  4. A task is past its start date with no time logged
 *
 * Deployment-safe:
 *  - localStorage is versioned; stale data from old deploys is auto-cleared
 *  - Handles SW update lifecycle (controllerchange) to keep the active SW ref fresh
 *  - serviceWorker.ready has a timeout so a broken SW install never hangs the app
 *  - Message listener is attached exactly once, regardless of how many times
 *    registerServiceWorker() is called
 */

import { getMasterAPI } from '@/core/api/api.service';

// ─── Config ──────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5 * 60 * 1000;   // 5 minutes
const STALE_THRESHOLD_HOURS = 24;
const SW_READY_TIMEOUT_MS = 10_000;        // give SW 10s to activate before giving up
const OVERDUE_NOTIFICATION_THRESHOLD = 3;  // OS notification when overdue count crosses this

// Due-soon: alert when task is due within this many hours
const DUE_SOON_HOURS = 24;
// Re-alert cooldown for due-soon (more urgent, shorter cooldown)
const DUE_SOON_COOLDOWN_HOURS = 4;
// Re-alert cooldown for no-progress alert
const NO_PROGRESS_COOLDOWN_HOURS = 24;

/**
 * Bump this whenever the localStorage schema changes between releases.
 * On mismatch the service clears all stored notification state so users
 * don't see phantom or missing notifications from stale data.
 */
const LS_SCHEMA_VERSION = '4';
const LS_SCHEMA_KEY       = 'tn_schema_v';
const LS_KNOWN_TASKS      = 'tn_known_task_ids';
const LS_LAST_UPDATES     = 'tn_last_task_updates';
const LS_LAST_NOTIFIED    = 'tn_last_stale_notified';
const LS_LAST_PENDING     = 'tn_last_pending_notified'; // due-soon + no-progress + sprint + bug + dep alerts
const LS_PERMISSION_ASKED = 'tn_permission_asked';
const SS_GREETING_SHOWN   = 'tn_greeting_shown';

const CRITICAL_BUG_COOLDOWN_HOURS = 4;
const DEP_RESOLVED_COOLDOWN_HOURS = 8;
const SPRINT_ENDING_DAYS_THRESHOLD = 2;

// ─── Internal state ───────────────────────────────────────────────────────────

let pollTimer: ReturnType<typeof setInterval> | null = null;
let currentUserId: string | null = null;
let currentDocType: string | null = null;
let swRegistration: ServiceWorkerRegistration | null = null;
let messageListenerAttached = false;
let prevOverdueCount: number | null = null; // tracks crossing edge for volume threshold

// ─── LocalStorage schema migration ───────────────────────────────────────────

function migrateLocalStorage() {
  const stored = localStorage.getItem(LS_SCHEMA_KEY);
  if (stored === LS_SCHEMA_VERSION) return; // already on current schema

  console.log(
    `[Notifications] localStorage schema mismatch (stored="${stored}", expected="${LS_SCHEMA_VERSION}"). Clearing stale notification state.`
  );
  try {
    localStorage.removeItem(LS_KNOWN_TASKS);
    localStorage.removeItem(LS_LAST_UPDATES);
    localStorage.removeItem(LS_LAST_NOTIFIED);
    localStorage.removeItem(LS_LAST_PENDING);
    localStorage.setItem(LS_SCHEMA_KEY, LS_SCHEMA_VERSION);
  } catch {
    // quota or private-browsing — continue anyway
  }
}

// ─── Service Worker registration ─────────────────────────────────────────────

/**
 * Registers the SW and waits for it to be fully active.
 * Safe to call multiple times — message listener is attached exactly once.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Notifications] Service workers not supported in this browser.');
    return null;
  }

  // Clear stale localStorage from old app versions
  migrateLocalStorage();

  try {
    console.log('[Notifications] Registering service worker /sw.js …');
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    // Wait for SW to be active with a timeout so a broken SW never hangs the app
    const reg = await Promise.race<ServiceWorkerRegistration | null>([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) =>
        setTimeout(() => {
          console.warn(`[Notifications] serviceWorker.ready timed out after ${SW_READY_TIMEOUT_MS}ms. Continuing without guaranteed active SW.`);
          resolve(null);
        }, SW_READY_TIMEOUT_MS)
      ),
    ]);

    if (reg) {
      swRegistration = reg;
      console.log('[Notifications] SW registered & active. State:', reg.active?.state, '| Version from SW:', SW_READY_TIMEOUT_MS);
    } else {
      // Timeout path: try to get whatever is available
      const existing = await navigator.serviceWorker.getRegistration('/');
      swRegistration = existing ?? null;
      console.warn('[Notifications] Using fallback SW registration:', !!swRegistration);
    }

    // Attach message listener exactly once
    if (!messageListenerAttached) {
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
      messageListenerAttached = true;
      console.log('[Notifications] SW message listener attached.');
    }

    // When a new SW deploys and takes over, update our reference so messages
    // keep going to the right worker.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[Notifications] SW updated (controllerchange). Refreshing registration reference…');
      navigator.serviceWorker.ready.then((newReg) => {
        swRegistration = newReg;
        console.log('[Notifications] SW reference updated. New state:', newReg.active?.state);
      });
    });

    return swRegistration;
  } catch (err) {
    console.error('[Notifications] SW registration failed:', err);
    return null;
  }
}

function handleSwMessage(event: MessageEvent) {
  const { type, url, version } = event.data || {};

  if (type === 'NOTIFICATION_CLICKED' && url) {
    console.log('[Notifications] Notification clicked → navigate to', url);
    window.location.href = url;
    return;
  }

  if (type === 'SW_ACTIVATED') {
    console.log('[Notifications] New SW activated. Version:', version, '→ refreshing registration.');
    navigator.serviceWorker.ready.then((reg) => {
      swRegistration = reg;
    });
    return;
  }

  if (type === 'SW_VERSION') {
    console.log('[Notifications] SW version:', version);
  }
}

// ─── Permission ───────────────────────────────────────────────────────────────

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[Notifications] Notification API not available in this browser.');
    return 'denied';
  }
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') {
    console.warn('[Notifications] Permission was previously denied by the user.');
    return 'denied';
  }

  // One prompt per session — prevents the browser from treating repeated prompts as spam
  const alreadyAsked = sessionStorage.getItem(LS_PERMISSION_ASKED);
  if (alreadyAsked) {
    console.log('[Notifications] Permission already asked this session, skipping.');
    return Notification.permission;
  }
  sessionStorage.setItem(LS_PERMISSION_ASKED, '1');

  console.log('[Notifications] Requesting notification permission…');
  const result = await Notification.requestPermission();
  console.log('[Notifications] Permission result:', result);
  return result;
}

export function hasNotificationPermission(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

// ─── Send notification via SW ─────────────────────────────────────────────────

interface ExtendedNotificationOptions extends NotificationOptions {
  vibrate?: number[];
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

function buildNotificationOptions(
  type: 'SHOW_NOTIFICATION' | 'SHOW_PERSISTENT_NOTIFICATION',
  payload: { body: string; tag?: string; url?: string }
): ExtendedNotificationOptions {
  const base: ExtendedNotificationOptions = {
    body: payload.body,
    tag: payload.tag || 'task-notification',
    icon: '/favicon-32x32.png',
    badge: '/favicon-32x32.png',
    data: { url: payload.url || '/' },
    silent: false,
    vibrate: [200, 100, 200],
  };
  if (type === 'SHOW_PERSISTENT_NOTIFICATION') {
    return {
      ...base,
      requireInteraction: true,
      actions: [
        { action: 'open', title: 'Open Task' },
        { action: 'dismiss', title: 'Dismiss' },
      ],
    };
  }
  return { ...base, requireInteraction: false };
}

/**
 * Reliable multi-path notification dispatch:
 *  1. registration.showNotification() — works even if SW is not page controller
 *  2. postMessage to SW active worker — secondary path
 *  3. Skip (Chrome blocks new Notification() from page context)
 * If SW is not yet active, waits up to 4 s for it to become ready.
 */
function sendToSW(
  type: 'SHOW_NOTIFICATION' | 'SHOW_PERSISTENT_NOTIFICATION',
  payload: { title: string; body: string; tag?: string; url?: string }
): void {
  if (!hasNotificationPermission()) {
    console.warn('[Notifications] sendToSW blocked — permission not granted. Current:', Notification.permission);
    return;
  }

  const dispatch = async () => {
    const options = buildNotificationOptions(type, payload);

    // Resolve registration — wait up to 4 s if not yet available
    let reg = swRegistration;
    if (!reg?.active) {
      try {
        reg = await Promise.race<ServiceWorkerRegistration>([
          navigator.serviceWorker.ready,
          new Promise<ServiceWorkerRegistration>((_, reject) =>
            setTimeout(() => reject(new Error('SW ready timeout')), 4000)
          ),
        ]);
        swRegistration = reg;
      } catch {
        console.warn('[Notifications] SW not ready within 4 s.');
        reg = swRegistration; // keep whatever we have
      }
    }

    console.log('[Notifications] sendToSW →', type, '| title:', payload.title);
    console.log('[Notifications]   reg.active:', !!reg?.active, '| controller:', !!navigator.serviceWorker?.controller);

    // Path 1: registration.showNotification() — most reliable, no controller needed
    if (reg?.active) {
      try {
        await reg.showNotification(payload.title, options);
        console.log('[Notifications]   ✓ Shown via registration.showNotification()');
        return;
      } catch (err) {
        console.warn('[Notifications]   registration.showNotification() failed:', err);
      }
    }

    // Path 2: postMessage to active worker
    const sw = reg?.active ?? navigator.serviceWorker?.controller;
    if (sw) {
      sw.postMessage({ type, ...payload });
      console.log('[Notifications]   ✓ Message posted to SW (postMessage path)');
      return;
    }

    console.error('[Notifications]   All SW paths unavailable — notification not shown.');
  };

  dispatch().catch((err) => console.error('[Notifications] sendToSW unhandled error:', err));
}

// ─── Greeting notification ────────────────────────────────────────────────────

export function sendGreetingNotification(firstName: string) {
  if (sessionStorage.getItem(SS_GREETING_SHOWN)) {
    console.log('[Notifications] Greeting already shown this session, skipping.');
    return;
  }

  const hour = new Date().getHours();
  const timeGreeting =
    hour < 12 ? 'Good morning' :
    hour < 17 ? 'Good afternoon' :
    'Good evening';

  const name = firstName?.trim() || 'there';
  const title = `${timeGreeting}, ${name}! 👋`;

  console.log('[Notifications] Sending greeting:', title);

  sendToSW('SHOW_NOTIFICATION', {
    title,
    body: "Welcome back. You'll be notified about new tasks and reminders.",
    tag: 'greeting',
    url: '/',
  });

  sessionStorage.setItem(SS_GREETING_SHOWN, '1');
}

// ─── Persistence helpers ──────────────────────────────────────────────────────

function loadKnownTaskIds(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KNOWN_TASKS);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch { return new Set(); }
}

function saveKnownTaskIds(ids: Set<string>) {
  try { localStorage.setItem(LS_KNOWN_TASKS, JSON.stringify([...ids])); } catch { /* quota */ }
}

function loadLastUpdates(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LS_LAST_UPDATES);
    return raw ? JSON.parse(raw) as Record<string, string> : {};
  } catch { return {}; }
}

function saveLastUpdates(updates: Record<string, string>) {
  try { localStorage.setItem(LS_LAST_UPDATES, JSON.stringify(updates)); } catch { /* quota */ }
}

function loadLastNotified(): Record<string, number> {
  try {
    const raw = localStorage.getItem(LS_LAST_NOTIFIED);
    return raw ? JSON.parse(raw) as Record<string, number> : {};
  } catch { return {}; }
}

function saveLastNotified(map: Record<string, number>) {
  try { localStorage.setItem(LS_LAST_NOTIFIED, JSON.stringify(map)); } catch { /* quota */ }
}

function loadLastPendingNotified(): Record<string, number> {
  try {
    const raw = localStorage.getItem(LS_LAST_PENDING);
    return raw ? JSON.parse(raw) as Record<string, number> : {};
  } catch { return {}; }
}

function saveLastPendingNotified(map: Record<string, number>) {
  try { localStorage.setItem(LS_LAST_PENDING, JSON.stringify(map)); } catch { /* quota */ }
}

// ─── New check: sprint ending soon ───────────────────────────────────────────

async function checkSprintEndingSoon(pendingMap: Record<string, number>, now: number): Promise<void> {
  try {
    const response = await getMasterAPI("SPR", "status=ACTIVE&pageSize=50&pageIndex=0");
    const sprints = (
      response?.data as { data?: Array<{ docNo: string; description?: string; endDate?: string }> } | undefined
    )?.data ?? [];

    for (const sprint of sprints) {
      if (!sprint.endDate) continue;
      const daysRemaining = (new Date(sprint.endDate).getTime() - now) / (1_000 * 60 * 60 * 24);

      if (daysRemaining > 0 && daysRemaining <= SPRINT_ENDING_DAYS_THRESHOLD) {
        const today = new Date().toISOString().split("T")[0];
        const key = `sprint-ending:${sprint.docNo}:${today}`;
        if (!pendingMap[key]) {
          const daysLabel = daysRemaining <= 1 ? "tomorrow" : "in 2 days";
          sendToSW("SHOW_PERSISTENT_NOTIFICATION", {
            title: "🏃 Sprint Ending Soon",
            body: `Sprint "${sprint.description ?? sprint.docNo}" ends ${daysLabel}. Complete or move unfinished tasks.`,
            tag: `sprint-ending-${sprint.docNo}`,
            url: `/master/SPR/view/${sprint.docNo}`,
          });
          pendingMap[key] = now;
        }
      }
    }
  } catch (err) {
    console.warn("[Notifications] checkSprintEndingSoon failed:", err);
  }
}

// ─── New check: critical bug assigned ────────────────────────────────────────

function checkCriticalBugAssigned(
  tasks: Array<{ docNo: string; description?: string; taskType?: string; severity?: string }>,
  pendingMap: Record<string, number>,
  now: number,
  docType: string
): void {
  for (const task of tasks) {
    if (task.taskType?.toUpperCase() !== "BUG") continue;
    if (task.severity?.toUpperCase() !== "CRITICAL") continue;

    const key = `critical-bug:${task.docNo}`;
    const hoursSince = (now - (pendingMap[key] || 0)) / (1_000 * 60 * 60);

    if (hoursSince >= CRITICAL_BUG_COOLDOWN_HOURS) {
      sendToSW("SHOW_PERSISTENT_NOTIFICATION", {
        title: "🐛 Critical Bug Assigned",
        body: `"${task.description || `Task #${task.docNo}`}" — Critical severity bug requires immediate attention.`,
        tag: `critical-bug-${task.docNo}`,
        url: `/master/${docType}/edit/${task.docNo}`,
      });
      pendingMap[key] = now;
    }
  }
}

// ─── New check: dependency resolved ──────────────────────────────────────────

function checkDependencyResolved(
  tasks: Array<{ docNo: string; description?: string; isBlocked?: boolean }>,
  pendingMap: Record<string, number>,
  now: number,
  docType: string
): void {
  for (const task of tasks) {
    const prevKey = `blocked-prev:${task.docNo}`;
    const wasBlocked = pendingMap[prevKey] === 1;
    const isNowBlocked = task.isBlocked ?? false;

    if (wasBlocked && !isNowBlocked) {
      const resolvedKey = `dep-resolved:${task.docNo}`;
      const hoursSince = (now - (pendingMap[resolvedKey] || 0)) / (1_000 * 60 * 60);

      if (hoursSince >= DEP_RESOLVED_COOLDOWN_HOURS) {
        sendToSW("SHOW_NOTIFICATION", {
          title: "✅ Blocker Resolved",
          body: `"${task.description || `Task #${task.docNo}`}" is no longer blocked — you can proceed.`,
          tag: `dep-resolved-${task.docNo}`,
          url: `/master/${docType}/edit/${task.docNo}`,
        });
        pendingMap[resolvedKey] = now;
      }
    }

    // Persist current blocked state for next poll comparison
    pendingMap[prevKey] = isNowBlocked ? 1 : 0;
  }
}

// ─── Core poll logic ─────────────────────────────────────────────────────────

async function pollTasks() {
  console.log('[Notifications] pollTasks() — userId:', currentUserId, '| docType:', currentDocType);

  if (!currentUserId || !currentDocType) {
    console.warn('[Notifications] Poll skipped — userId/docType not set.');
    return;
  }
  if (!hasNotificationPermission()) {
    console.warn('[Notifications] Poll skipped — permission:', Notification.permission);
    return;
  }

  let tasks: Array<{
    docNo: string;
    description: string;
    assignedTo: string;
    updatedTs: string | null;
    createdTs: string;
    status: string;
    dueDate: string | null;
    startDate: string | null;
    totalTimeHrs: number | null;
    taskType?: string;
    severity?: string;
    isBlocked?: boolean;
  }>;

  try {
    const params = `assignedTo=${currentUserId}&pageSize=100&pageIndex=0`;
    console.log('[Notifications] Fetching tasks:', params);
    const response = await getMasterAPI(currentDocType, params);
    tasks = (response?.data as { data?: typeof tasks } | undefined)?.data ?? [];
    console.log('[Notifications] Fetched', tasks.length, 'task(s).');
  } catch (err) {
    console.error('[Notifications] Task fetch failed (network?):', err);
    return;
  }

  const knownIds          = loadKnownTaskIds();
  const lastUpdates       = loadLastUpdates();
  const lastNotified      = loadLastNotified();
  const lastPending       = loadLastPendingNotified();
  const now               = Date.now();

  const newKnownIds       = new Set(knownIds);
  const newLastUpdates    = { ...lastUpdates };
  const newLastNotified   = { ...lastNotified };
  const newLastPending    = { ...lastPending };

  let newCount      = 0;
  let staleCount    = 0;
  let dueSoonCount  = 0;
  let noProgCount   = 0;

  for (const task of tasks) {
    const assignees = (task.assignedTo || '').split(',').map((s: string) => s.trim());
    if (!assignees.includes(currentUserId!)) continue;

    const desc = task.description || `Task #${task.docNo}`;
    const taskUrl = `/master/${currentDocType}/edit/${task.docNo}`;

    // ── 1. New task assigned ──────────────────────────────────────────────────
    if (!knownIds.has(task.docNo)) {
      console.log('[Notifications] NEW task detected:', task.docNo, '-', desc.slice(0, 40));
      sendToSW('SHOW_NOTIFICATION', {
        title: '📋 New Task Assigned',
        body: desc,
        tag: `new-task-${task.docNo}`,
        url: taskUrl,
      });
      newCount++;
    }
    newKnownIds.add(task.docNo);

    const lastActivity = task.updatedTs || task.createdTs;
    const isTerminal   = /completed|done|cancelled/i.test(task.status);

    if (isTerminal) {
      newLastUpdates[task.docNo] = lastActivity;
      continue;
    }

    // ── 2. Stale task — no update for 24 h (12 h re-alert cooldown) ──────────
    const hoursIdle = (now - new Date(lastActivity).getTime()) / (1_000 * 60 * 60);

    if (hoursIdle >= STALE_THRESHOLD_HOURS) {
      const lastNotifiedAt     = lastNotified[task.docNo] || 0;
      const hoursSinceNotified = (now - lastNotifiedAt) / (1_000 * 60 * 60);

      console.log(
        `[Notifications] Task ${task.docNo} idle ${hoursIdle.toFixed(1)}h` +
        ` | last stale-alerted ${hoursSinceNotified.toFixed(1)}h ago`
      );

      if (hoursSinceNotified >= 12) {
        const daysIdle  = Math.floor(hoursIdle / 24);
        const idleLabel = daysIdle >= 1 ? `${daysIdle}d` : `${Math.round(hoursIdle)}h`;
        const isOverdue = task.dueDate && new Date(task.dueDate) < new Date();

        sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
          title: isOverdue ? '⚠️ Overdue Task Needs Update' : '🔔 Task Progress Update Needed',
          body: `"${desc}" — no update for ${idleLabel}. Please log your progress.`,
          tag: `stale-task-${task.docNo}`,
          url: taskUrl,
        });
        newLastNotified[task.docNo] = now;
        staleCount++;
      }
    }

    // ── 3. Due soon — due within 24 h (4 h re-alert cooldown) ────────────────
    if (task.dueDate) {
      const hoursUntilDue = (new Date(task.dueDate).getTime() - now) / (1_000 * 60 * 60);
      if (hoursUntilDue > 0 && hoursUntilDue <= DUE_SOON_HOURS) {
        const pendingKey       = `due:${task.docNo}`;
        const lastAlertAt      = newLastPending[pendingKey] || 0;
        const hoursSinceAlert  = (now - lastAlertAt) / (1_000 * 60 * 60);

        console.log(
          `[Notifications] Task ${task.docNo} due in ${hoursUntilDue.toFixed(1)}h` +
          ` | last due-alerted ${hoursSinceAlert.toFixed(1)}h ago`
        );

        if (hoursSinceAlert >= DUE_SOON_COOLDOWN_HOURS) {
          const dueLabel =
            hoursUntilDue <= 2  ? 'within 2 hours' :
            hoursUntilDue <= 4  ? 'within 4 hours' :
            hoursUntilDue <= 8  ? 'today'           :
                                  'tomorrow';

          sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
            title: '⏰ Task Due Soon',
            body: `"${desc}" is due ${dueLabel}. Please complete or update.`,
            tag: `due-soon-${task.docNo}`,
            url: taskUrl,
          });
          newLastPending[pendingKey] = now;
          dueSoonCount++;
        }
      }
    }

    // ── 4. No progress logged — past start date, 0 time logged (24 h cooldown) ─
    const noTimeLogged = !task.totalTimeHrs || task.totalTimeHrs <= 0;
    if (task.startDate && noTimeLogged && new Date(task.startDate).getTime() < now) {
      const pendingKey      = `noprog:${task.docNo}`;
      const lastAlertAt     = newLastPending[pendingKey] || 0;
      const hoursSinceAlert = (now - lastAlertAt) / (1_000 * 60 * 60);

      console.log(
        `[Notifications] Task ${task.docNo} past start date, 0 h logged` +
        ` | last noprog-alerted ${hoursSinceAlert.toFixed(1)}h ago`
      );

      if (hoursSinceAlert >= NO_PROGRESS_COOLDOWN_HOURS) {
        sendToSW('SHOW_NOTIFICATION', {
          title: '📊 No Progress Logged',
          body: `"${desc}" has no time logged yet. Please update your progress.`,
          tag: `no-progress-${task.docNo}`,
          url: taskUrl,
        });
        newLastPending[pendingKey] = now;
        noProgCount++;
      }
    }

    newLastUpdates[task.docNo] = lastActivity;
  }

  // ── 5-7. Extended checks (sprint-ending, critical-bug, dependency-resolved) ──
  const myTasks = tasks.filter((t) => {
    const assignees = (t.assignedTo || '').split(',').map((s) => s.trim());
    return assignees.includes(currentUserId!);
  });

  checkCriticalBugAssigned(myTasks, newLastPending, now, currentDocType!);
  checkDependencyResolved(myTasks, newLastPending, now, currentDocType!);
  await checkSprintEndingSoon(newLastPending, now);

  // ── 8. Volume threshold: fire OS notification on crossing edge ───────────────
  const currentOverdueCount = tasks.filter(t => {
    const assignees = (t.assignedTo || '').split(',').map((s) => s.trim());
    if (!assignees.includes(currentUserId!)) return false;
    if (!t.dueDate) return false;
    if (/completed|done|cancelled/i.test(t.status)) return false;
    return new Date(t.dueDate) < new Date(new Date().toDateString());
  }).length;

  if (
    hasNotificationPermission() &&
    prevOverdueCount !== null &&
    prevOverdueCount < OVERDUE_NOTIFICATION_THRESHOLD &&
    currentOverdueCount >= OVERDUE_NOTIFICATION_THRESHOLD
  ) {
    sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
      title: '⚠️ Task Alert',
      body: `You have ${currentOverdueCount} overdue tasks. Please review and update your progress.`,
      tag: 'volume-overdue-threshold',
      url: '/master/TSK',
    });
  }
  prevOverdueCount = currentOverdueCount;

  console.log(
    `[Notifications] Poll done.` +
    ` New: ${newCount} | Stale: ${staleCount} | Due-soon: ${dueSoonCount} | No-progress: ${noProgCount}` +
    ` | Overdue: ${currentOverdueCount}`
  );

  saveKnownTaskIds(newKnownIds);
  saveLastUpdates(newLastUpdates);
  saveLastNotified(newLastNotified);
  saveLastPendingNotified(newLastPending);
}

// ─── Public API ───────────────────────────────────────────────────────────────

let visibilityListenerAttached = false;

function handleVisibilityChange() {
  if (document.visibilityState !== 'visible' || !currentUserId) return;
  console.log('[Notifications] Tab became visible — triggering immediate poll and resetting interval.');
  pollTasks();
  // Restart the interval so the next poll is a full POLL_INTERVAL_MS after this one
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = setInterval(pollTasks, POLL_INTERVAL_MS);
  }
}

export function startTaskNotificationPolling(userId: string, docType: string) {
  if (pollTimer) stopTaskNotificationPolling();

  currentUserId  = userId;
  currentDocType = docType;

  console.log('[Notifications] Polling started — userId:', userId, '| docType:', docType, '| interval:', POLL_INTERVAL_MS / 1000, 's');

  if (!visibilityListenerAttached) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = true;
  }

  pollTasks(); // immediate first poll
  pollTimer = setInterval(pollTasks, POLL_INTERVAL_MS);
}

export function stopTaskNotificationPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
    console.log('[Notifications] Polling stopped.');
  }
  if (visibilityListenerAttached) {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = false;
  }
  currentUserId  = null;
  currentDocType = null;
  prevOverdueCount = null;
}

/** Trigger an out-of-band poll (e.g. when task list reloads). */
export function triggerImmediatePoll() {
  console.log('[Notifications] Manual poll triggered.');
  pollTasks();
}

/** Call on logout — stops polling and wipes stored state. */
export function clearNotificationState() {
  stopTaskNotificationPolling();
  try {
    localStorage.removeItem(LS_KNOWN_TASKS);
    localStorage.removeItem(LS_LAST_UPDATES);
    localStorage.removeItem(LS_LAST_NOTIFIED);
    localStorage.removeItem(LS_LAST_PENDING);
    // Clear greeting flag so the next login always fires the greeting notification
    sessionStorage.removeItem(SS_GREETING_SHOWN);
    // Keep schema version key so migration doesn't re-run next login
    console.log('[Notifications] Stored notification state cleared.');
  } catch { /* ignore */ }
}

// ─── Dev / debug helpers (callable from browser console) ─────────────────────

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__notifDebug = {
    /** Print all stored notification state */
    dump: () => {
      console.group('[Notifications] Stored state');
      console.log('Schema version:', localStorage.getItem(LS_SCHEMA_KEY));
      console.log('Permission:', 'Notification' in window ? Notification.permission : 'N/A');
      console.log('SW active:', !!swRegistration?.active);
      console.log('SW controller:', !!navigator.serviceWorker?.controller);
      console.log('Polling running:', !!pollTimer);
      console.log('Known task IDs:', loadKnownTaskIds());
      console.log('Last updates:', loadLastUpdates());
      console.log('Last stale-notified:', loadLastNotified());
      console.log('Last pending-notified:', loadLastPendingNotified());
      console.groupEnd();
    },
    /** Reset everything and reload — use to simulate a fresh first-visit */
    reset: () => {
      localStorage.removeItem(LS_KNOWN_TASKS);
      localStorage.removeItem(LS_LAST_UPDATES);
      localStorage.removeItem(LS_LAST_NOTIFIED);
      localStorage.removeItem(LS_LAST_PENDING);
      localStorage.removeItem(LS_SCHEMA_KEY);
      sessionStorage.removeItem(LS_PERMISSION_ASKED);
      sessionStorage.removeItem(SS_GREETING_SHOWN);
      console.log('[Notifications] All state cleared. Reload the page to re-run init.');
    },
    /** Force-fire greeting (ignores the session dedup flag) */
    testGreeting: (name = 'Test') => {
      sessionStorage.removeItem(SS_GREETING_SHOWN);
      sendGreetingNotification(name);
    },
    /** Force-fire a new-task notification */
    testNewTask: () => {
      sendToSW('SHOW_NOTIFICATION', {
        title: '📋 New Task Assigned',
        body: 'Test task — Debug notification from __notifDebug',
        tag: `debug-new-task-${Date.now()}`,
        url: '/',
      });
    },
    /** Force-fire a stale/overdue notification */
    testStale: (overdue = false) => {
      sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
        title: overdue ? '⚠️ Overdue Task Needs Update' : '🔔 Task Progress Update Needed',
        body: 'Test task — no update for 2d. Please log your progress. (Debug)',
        tag: `debug-stale-task-${Date.now()}`,
        url: '/',
      });
    },
    /** Force-fire a due-soon notification */
    testDueSoon: () => {
      sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
        title: '⏰ Task Due Soon',
        body: 'Test task — due within 2 hours. Please complete or update. (Debug)',
        tag: `debug-due-soon-${Date.now()}`,
        url: '/',
      });
    },
    /** Force-fire a no-progress notification */
    testNoProgress: () => {
      sendToSW('SHOW_NOTIFICATION', {
        title: '📊 No Progress Logged',
        body: 'Test task — no time logged yet. Please update your progress. (Debug)',
        tag: `debug-no-progress-${Date.now()}`,
        url: '/',
      });
    },
    /** Trigger an immediate poll right now */
    poll: () => pollTasks(),
    /** Force-fire a sprint-ending-soon notification */
    testSprintEnding: () => {
      sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
        title: '🏃 Sprint Ending Soon',
        body: 'Sprint "Sprint 5" ends tomorrow. Complete or move unfinished tasks. (Debug)',
        tag: `debug-sprint-ending-${Date.now()}`,
        url: '/',
      });
    },
    /** Force-fire a critical bug notification */
    testCriticalBug: () => {
      sendToSW('SHOW_PERSISTENT_NOTIFICATION', {
        title: '🐛 Critical Bug Assigned',
        body: 'Test critical bug — requires immediate attention. (Debug)',
        tag: `debug-critical-bug-${Date.now()}`,
        url: '/',
      });
    },
    /** Force-fire a dependency-resolved notification */
    testDepResolved: () => {
      sendToSW('SHOW_NOTIFICATION', {
        title: '✅ Blocker Resolved',
        body: 'Test task — no longer blocked, you can proceed. (Debug)',
        tag: `debug-dep-resolved-${Date.now()}`,
        url: '/',
      });
    },
  };
  console.log(
    '[Notifications] Debug helpers: window.__notifDebug' +
    '.dump() | .reset() | .testGreeting() | .testNewTask() | .testStale() | .testDueSoon() | .testNoProgress()' +
    ' | .testSprintEnding() | .testCriticalBug() | .testDepResolved() | .poll()'
  );
}
